import { TimeMinutesFormat } from '@/models/common';
import { PreOrder } from '@/models/order';
import { getTotalDuratonBySKUSpecifications } from '@/models/sku';
import { Flex, Progress } from 'antd';
import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { useEffect, useState } from 'react';

const SERVICE_STATUS = {
  NOT_STARTED: 0,
  RUNNING: 1,
  BUFFERING: 2,
  TIMEOUT: 3,
} as const;
export type ServiceStatus = (typeof SERVICE_STATUS)[keyof typeof SERVICE_STATUS];
export const SERVICE_STATUS_MAP: Record<ServiceStatus, { label: string; color?: string }> = {
  [SERVICE_STATUS.NOT_STARTED]: { label: '未开始', color: 'gray' },
  [SERVICE_STATUS.RUNNING]: { label: '进行中', color: 'blue' },
  [SERVICE_STATUS.BUFFERING]: { label: '进行中', color: 'blue' },
  [SERVICE_STATUS.TIMEOUT]: { label: '已超时', color: 'red' },
};

export const useServiceTimerInfo = (preOrder: PreOrder, isAvailable: boolean) => {
  const specifications = preOrder.specifications;
  const totalDuration = getTotalDuratonBySKUSpecifications(specifications);
  const endTime = preOrder.startTime ? dayjs(preOrder.startTime).add(totalDuration, 'minute') : undefined;

  // 从开始时间到当前时间的 duration (已服务时长)
  const [elapsedDuration, updateElapsedDuration] = useState<Duration>();

  useEffect(() => {
    if (!isAvailable || !preOrder.startTime) return;
    const updater = () => {
      updateElapsedDuration(dayjs.duration(dayjs().diff(preOrder.startTime)));
    };
    const timer = setInterval(updater, 1000);
    updater();
    return () => {
      clearInterval(timer);
    };
  }, [preOrder.startTime, isAvailable]);

  const notStart = !preOrder.startTime || !elapsedDuration;

  let status: ServiceStatus;
  if (!preOrder.startTime) {
    status = SERVICE_STATUS.NOT_STARTED;
  } else if (dayjs().isBefore(endTime)) {
    status = SERVICE_STATUS.RUNNING;
  } else if (!dayjs().isAfter(endTime.add(totalDuration * 0.15, 'minutes'))) {
    // 当前时间小于 预定结束时间 + 15% 的服务时长 = 缓冲期
    status = SERVICE_STATUS.BUFFERING;
  } else {
    status = SERVICE_STATUS.TIMEOUT;
  }

  return { status, elapsedDuration, notStart, totalDuration, endTime };
};

/**
 * 服务计时器
 * 始终按照商品设定的预计时长计算
 * @param param0
 * @returns
 */
export const ServiceTimer = ({
  elapsedDuration,
  notStart,
  totalDuration,
  endTime,
}: ReturnType<typeof useServiceTimerInfo>) => {
  const percent = Math.min(((elapsedDuration?.asMinutes() ?? 0) / totalDuration) * 100, 100);
  return (
    <Flex gap={8} vertical={false} justify="space-between" align="center">
      <Progress
        size="small"
        steps={6}
        percent={percent}
        format={() => {
          if (notStart) {
            return <>未开始</>;
          }
          const minutes = Math.floor(elapsedDuration.asMinutes());
          const seconds = elapsedDuration.seconds();
          const display = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

          return (
            <>
              <span className="font-monospace">{display}</span>
              <span> / </span>
              <span className="font-monospace">{totalDuration}</span>
              分钟
            </>
          );
        }}
      />
      {!notStart && endTime ? (
        <div>
          计划完成: <span className="font-monospace">{endTime?.format(TimeMinutesFormat)}</span>
        </div>
      ) : (
        <div>
          时长: <span className="font-monospace">{totalDuration}</span>分钟
        </div>
      )}
    </Flex>
  );
};
